/**
 * Detail view for OfflineMeeting explore entries.
 * Mirrors ExploreContentViewOnlineMeeting but adds location, registration form,
 * remaining seats, and subscribe/waitlist flow matching kinasticCoach.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { OfflineMeetingEntry } from '../../../Model/Explore/OfflineMeetingEntry';
import { ExploreContentViewHostCard } from '../ExploreContentViewHostCard';
import { ExploreContentViewOfflineMeetingContent } from './ExploreContentViewOfflineMeetingContent';
import { ExploreContentViewOfflineMeetingRegistration } from './ExploreContentViewOfflineMeetingRegistration';
import { useRootStore } from '../../../Store/useRootStore';
import { useLocalized } from '../../../Hooks/useLocalized';
import { ShareButton } from '../../ShareButton';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { useLocalizedImage } from '../../../Hooks/useLocalizedMedia';
import { getMediaLargeOrMediumOrSmallest } from '../../../Model/Media/Media';
import { Theme } from '../../../Theme/Theme';
import { useEffect } from 'react';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
`;

const HeroSection = styled.div<{ bgUrl?: string }>`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 0;
  @media (max-width: 768px) {
    padding: 16px 16px 0;
  }
`;

const HeroImage = styled.div<{ bgUrl?: string }>`
  width: 100%;
  height: 300px;
  border-radius: 16px 16px 0 0;
  background: ${({ bgUrl }) => (bgUrl ? `url('${bgUrl}')` : Theme.Colors.lightestGrey)};
  background-size: cover;
  background-position: center 30%;
  background-repeat: no-repeat;
  position: relative;

  @media (min-width: 600px) {
    height: 360px;
  }
  @media (min-width: 992px) {
    height: 480px;
  }
`;

const DateOverlay = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateCard = styled.div`
  background-color: #edeef0;
  border-radius: 8px;
  padding: 12px 16px;
  font-weight: 700;
  text-align: center;
  min-width: 80px;
`;

const DateMonth = styled.div`
  font-weight: 500;
  font-size: 16px;
  text-transform: uppercase;
`;

const DateDay = styled.div`
  font-size: 28px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 64px;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 24px 16px 48px;
  }
`;

const ContentCard = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 24px;
    gap: 24px;
  }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 280px;
  max-width: 300px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const RegistrationCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 20px;
  padding: 24px;
`;

export type ExploreContentViewOfflineMeetingProps = {
  entry: OfflineMeetingEntry;
};

export const ExploreContentViewOfflineMeeting: React.FC<ExploreContentViewOfflineMeetingProps> = observer(
  ({ entry }) => {
    const { analytics } = useRootStore();
    const title = useLocalized(entry.name);
    const description = useLocalized(entry.description);
    const entryImage = useLocalizedImage(entry.images);
    const imageUrl = entryImage ? getMediaLargeOrMediumOrSmallest(entryImage) : undefined;
    const nextDate = entry.nextDate;

    useContentMetaTags({ title, description });

    useEffect(() => {
      analytics.logSelectContent(entry.type, entry.id);
    }, [analytics, entry]);

    return (
      <PageContainer>
        <HeroSection>
          <HeroImage bgUrl={imageUrl}>
            {nextDate && (
              <DateOverlay>
                <DateCard>{nextDate.format('LT')}</DateCard>
                <DateCard>
                  <DateMonth>{nextDate.format('ddd')}</DateMonth>
                  <DateDay>{nextDate.format('DD')}</DateDay>
                  <DateMonth>{nextDate.format('MMM')}</DateMonth>
                </DateCard>
              </DateOverlay>
            )}
          </HeroImage>
        </HeroSection>

        <ContentContainer>
          <ContentCard>
            <MainContent>
              <ExploreContentViewOfflineMeetingContent entry={entry} />
            </MainContent>

            <SideContent>
              <ShareButton
                data={{ title, description, contentType: 'offlineMeeting', contentId: entry.id }}
              />
              <RegistrationCard>
                <ExploreContentViewOfflineMeetingRegistration entry={entry} />
              </RegistrationCard>
              {entry.hosts[0] && <ExploreContentViewHostCard host={entry.hosts[0]} />}
            </SideContent>
          </ContentCard>
        </ContentContainer>
      </PageContainer>
    );
  },
);
